/**
 * Lightweight module-level params store for repair navigation.
 *
 * Mirrors the pattern of React Navigation's route.params — the caller sets
 * params immediately before triggering navigation; the destination reads them
 * on mount.  Because RepairDetailsScreen is rendered inline inside
 * MainLayoutScreen (not via a stack navigate() call), this module-level
 * singleton is the idiomatic equivalent of navigation params for this flow.
 *
 * Persistence:
 *   On web the params are also written to sessionStorage so that a page reload
 *   (e.g. F5 / Cmd-R) does not lose the breadcrumb.  The in-memory variable
 *   acts as a fast synchronous cache; sessionStorage is the fallback that
 *   survives the reload.  On React Native sessionStorage is unavailable so we
 *   silently fall back to in-memory only.
 *
 * Lifecycle:
 *   - set()   called in DashboardScreen just before dispatching setSelectedRepairId
 *   - get()   called in RepairDetailsScreen to populate breadcrumb before API responds
 *   - clear() called in RepairDetailsScreen's cleanup effect on unmount
 */

export interface RepairNavParams {
  /** Display code strings */
  actionID: string;
  groupID: string;
  componentID: string;
  /** Numeric DB IDs */
  repairActionID: number;
  repairGroupID: number;
  repairComponentID: number;
}

const SESSION_KEY = 'repairNavParams';

function readSession(): RepairNavParams | null {
  try {
    if (typeof sessionStorage === 'undefined') return null;
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as RepairNavParams) : null;
  } catch {
    return null;
  }
}

function writeSession(params: RepairNavParams): void {
  try {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(params));
  } catch {
    // quota exceeded or private-browsing restriction — silently ignore
  }
}

function clearSession(): void {
  try {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // ignore
  }
}

// In-memory cache initialised from sessionStorage so that a hot-module reload
// (without a full page refresh) also picks up previously set params.
let _params: RepairNavParams | null = readSession();

export const repairNavParamsStore = {
  set(params: RepairNavParams): void {
    _params = params;
    writeSession(params);
  },
  get(): RepairNavParams | null {
    // Prefer in-memory; fall back to sessionStorage (covers page-reload case).
    if (_params) return _params;
    const fromSession = readSession();
    if (fromSession) {
      _params = fromSession; // warm the in-memory cache
    }
    return _params;
  },
  clear(): void {
    _params = null;
    clearSession();
  },
};